import { Checkbox, CounterPill, Icon, Input } from '@aon/aon-tws-common-ui';
import { FormattedTd } from '@aon/tws-common-ui-questions/build/lib/components/common';
import { SortableItem } from '@aon/tws-common-ui-questions/build/lib/components/common/SortableRow';
import * as React from 'react';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import { ICustomResponseOptionEditModel, ITranslationEditItem } from '../../../models';
import { TranslationEditor } from '../../translations/TranslationEditor';

export const NumberInput = styled(Input)`
	width: 80px;
	padding-right: 10px;
`;

export const TranslationEditorContainer = styled.div`
	margin-top: 30px;
	margin-bottom: 50px;
`;

export const ClickableIcon = styled.span<{ isDisabled?: boolean; fontSizeEm?: number }>`
	 {
		${(props) => (props.isDisabled ? 'color: #999999' : 'cursor: pointer')};
		padding-left: 5px;
		font-size: ${(props) => props.fontSizeEm || 1}em;
	}
`;

export interface IRowProps {
	responseOption: ICustomResponseOptionEditModel;
	disabled: boolean;
	isExpanded: boolean;
	defaultCultureCode: string;
	deleteDisabled: boolean;
	errorMessages: string[];
	index: number;
	HandleFollowUpCheck: (responseOptionId: number) => void;
	ToggleExpand: (id: number) => void;
	UpdateCustomResponseOptionValue: (value: number | '', responseOptionId: number) => void;
	UpdateCustomResponseOptionText: (text: string, cultureCode: string, responseOptionId: number) => void;
	DeleteResponseOption: (responseOptionId: number) => void;
}

export class CustomResponseOptionRow extends React.PureComponent<IRowProps> {
	constructor(props: IRowProps) {
		super(props);

		this.updateValue = this.updateValue.bind(this);
		this.handleUpdateTranslations = this.handleUpdateTranslations.bind(this);
	}

	public updateValue(e: any) {
		const value: string = e.target.value;
		const parsed = parseInt(value, 10);

		if (parsed > 0) {
			this.props.UpdateCustomResponseOptionValue(parsed, this.props.responseOption.id);
		}
	}

	public handleUpdateTranslations(newTranslations: ITranslationEditItem[]) {
		newTranslations.forEach((t) => {
			const roTranslation = this.props.responseOption.translations.find((rot) => rot.cultureCode === t.cultureCode);
			if (roTranslation && roTranslation.textValue !== t.textValue) {
				this.props.UpdateCustomResponseOptionText(t.textValue, roTranslation.cultureCode, this.props.responseOption.id);
			}
		});
	}

	public render() {
		const defaultTextValue: ITranslationEditItem = this.props.responseOption.translations.find(
			(t) => t.cultureCode === this.props.defaultCultureCode,
		) as ITranslationEditItem;
		const translations = this.props.responseOption.translations.filter((t) => t.cultureCode !== this.props.defaultCultureCode);
		const { errorMessages } = this.props;

		const textError =
			errorMessages.indexOf(ContentConstants.CUSTOM_RESPONSEOPTION_ERROR_LABEL_EMPTY) > -1 ||
			errorMessages.indexOf(ContentConstants.CUSTOM_RESPONSEOPTION_ERROR_LABEL_LENGTH_EXCEEDED) > -1;
		const valueError =
			errorMessages.indexOf(ContentConstants.CUSTOM_RESPONSEOPTION_ERROR_VALUE_DUPLICATE) > -1 ||
			errorMessages.indexOf(ContentConstants.CUSTOM_RESPONSEOPTION_ERROR_VALUE_EMPTY) > -1;
		return (
			<>
				<SortableItem
					id={this.props.responseOption.id + ''}
					hideLeftBorder={true}
					key={this.props.responseOption.id}
					index={this.props.index}
					sequence={this.props.responseOption.sequence}
					disabled={this.props.disabled}
				>
					<FormattedTd flexBasis={55}>
						<Input
							value={defaultTextValue.textValue}
							size='sm'
							maxLength={150}
							placeholder={ContentConstants.CUSTOM_RESPONSEOPTION_LABEL_PLACEHOLDER}
							onChange={(e: any) =>
								this.props.UpdateCustomResponseOptionText(e.target.value, this.props.defaultCultureCode, this.props.responseOption.id)
							}
							error={textError}
							message={this.getTextErrorMessage()}
						/>
					</FormattedTd>
					{translations.length > 0 && (
						<FormattedTd flexBasis={20}>
							<span>
								<CounterPill
									size='sm'
									expectedCount={translations.length}
									actualCount={translations.filter((t) => t.textValue).length}
									checkThreshold={true}
								/>
								<ClickableIcon
									onClick={() => this.props.ToggleExpand(this.props.responseOption.id)}
									fontSizeEm={0.8}
									className='tws-toggle-response-option-translations'
								>
									<Icon name={this.props.isExpanded ? 'angle-up' : 'angle-down'} />
								</ClickableIcon>
							</span>
						</FormattedTd>
					)}
					<FormattedTd flexBasis={15}>
						<NumberInput
							size='sm'
							type='number'
							value={this.props.responseOption.value + ''}
							onChange={this.updateValue}
							error={valueError}
							message={this.getValueErrorMessage()}
						/>
					</FormattedTd>
					<FormattedTd flexBasis={15}>
						<Checkbox
							checked={this.props.responseOption.hasFollowUp}
							onChange={() => this.props.HandleFollowUpCheck(this.props.responseOption.id)}
						/>
					</FormattedTd>
					<FormattedTd flexBasis={10} rightAlign={true}>
						<ClickableIcon
							isDisabled={this.props.deleteDisabled}
							fontSizeEm={1.5}
							className='tws-delete-response-option-button'
							onClick={() => this.props.deleteDisabled || this.props.DeleteResponseOption(this.props.responseOption.id)}
						>
							<Icon name='delete' />
						</ClickableIcon>
					</FormattedTd>
				</SortableItem>
				{this.props.isExpanded && (
					<TranslationEditorContainer>
						<TranslationEditor
							enableHtmlEditor={false}
							unStyledContainer={true}
							hasDefaultTranslation={defaultTextValue.textValue.trim().length > 0}
							property={'CustomResponseOptionEditModel'}
							propertyDisplayName={ContentConstants.TRANSLATIONS_RESPONSEOPTION}
							onChange={this.handleUpdateTranslations}
							translations={translations}
						/>
					</TranslationEditorContainer>
				)}
			</>
		);
	}

	private getTextErrorMessage() {
		const { errorMessages } = this.props;
		if (errorMessages.indexOf(ContentConstants.CUSTOM_RESPONSEOPTION_ERROR_LABEL_EMPTY) > -1) {
			return ContentConstants.CUSTOM_RESPONSEOPTION_ERROR_LABEL_EMPTY;
		} else if (errorMessages.indexOf(ContentConstants.CUSTOM_RESPONSEOPTION_ERROR_LABEL_LENGTH_EXCEEDED) > -1) {
			return ContentConstants.CUSTOM_RESPONSEOPTION_ERROR_LABEL_LENGTH_EXCEEDED;
		}

		return '';
	}

	private getValueErrorMessage() {
		const { errorMessages } = this.props;
		if (errorMessages.indexOf(ContentConstants.CUSTOM_RESPONSEOPTION_ERROR_VALUE_DUPLICATE) > -1) {
			return ContentConstants.CUSTOM_RESPONSEOPTION_ERROR_VALUE_DUPLICATE;
		} else if (errorMessages.indexOf(ContentConstants.CUSTOM_RESPONSEOPTION_ERROR_VALUE_EMPTY) > -1) {
			return ContentConstants.CUSTOM_RESPONSEOPTION_ERROR_VALUE_EMPTY;
		}

		return '';
	}
}
